import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  Chip
} from '@mui/material';
import {
  WorkspacePremium as SeniorityIcon,
  Work as PositionIcon,
  Business as DepartmentIcon,
  Category as ContractTypeIcon,
  CardGiftcard as BenefitIcon,
  Schedule as LeaveTypeIcon,
  AttachMoney as PayBandIcon,
  Security as PermissionIcon,
  Event as CareerEventIcon,
  Computer as EquipmentIcon,
  CardMembership as LicenseIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import './Settings.css';

const Settings = () => {
  const navigate = useNavigate();

  const referenceDataSections = [
    {
      title: 'HR Master Data',
      items: [
        {
          name: 'Seniority Levels',
          description: 'Manage employee seniority levels (Junior, Senior, Lead, etc.)',
          icon: <SeniorityIcon />,
          path: '/settings/seniority-levels',
          count: 8,
          color: '#1976d2'
        },
        {
          name: 'Positions',
          description: 'Manage job positions and titles',
          icon: <PositionIcon />,
          path: '/settings/positions',
          count: 15,
          color: '#388e3c'
        },
        {
          name: 'Departments',
          description: 'Manage organizational departments',
          icon: <DepartmentIcon />,
          path: '/settings/departments',
          count: 6,
          color: '#f57c00'
        }
      ]
    },
    {
      title: 'Employee Development',
      items: [
        {
          name: 'Career Event Types',
          description: 'Manage career event types (Reviews, Promotions, Training, etc.)',
          icon: <CareerEventIcon />,
          path: '/settings/career-event-types',
          count: 0,
          color: '#2196F3'
        }
      ]
    },
    {
      title: 'Contract Management',
      items: [
        {
          name: 'Contract Types',
          description: 'Manage contract types (Permanent, Temporary, etc.)',
          icon: <ContractTypeIcon />,
          path: '/settings/contract-types',
          count: 4,
          color: '#7b1fa2'
        },
        {
          name: 'Document Types',
          description: 'Manage document types (CV, ID Card, Contracts, etc.)',
          icon: <ContractTypeIcon />,
          path: '/settings/document-types',
          count: 0,
          color: '#0288d1'
        },
        {
          name: 'Benefit Types',
          description: 'Manage employee benefits and perks',
          icon: <BenefitIcon />,
          path: '/settings/benefit-types',
          count: 12,
          color: '#c2185b'
        },
        {
          name: 'Pay Bands',
          description: 'Manage salary bands and compensation levels',
          icon: <PayBandIcon />,
          path: '/settings/pay-bands',
          count: 10,
          color: '#00796b'
        }
      ]
    },
    {
      title: 'Equipment Management',
      items: [
        {
          name: 'Equipment Categories',
          description: 'Manage equipment categories',
          icon: <EquipmentIcon />,
          path: '/settings/equipment-categories',
          count: 0,
          color: '#546e7a'
        },
        {
          name: 'Equipment Item Types',
          description: 'Manage item types (subcategories) with estimated prices',
          icon: <EquipmentIcon />,
          path: '/settings/equipment-item-types',
          count: 0,
          color: '#607d8b'
        },
        {
          name: 'Equipment Inventory',
          description: 'Manage all equipment items (stock)',
          icon: <EquipmentIcon />,
          path: '/admin/equipment-inventory',
          count: 0,
          color: '#37474f'
        },
        {
          name: 'Equipment Assignments',
          description: 'Manage equipment assigned to employees',
          icon: <EquipmentIcon />,
          path: '/admin/equipment-assignments',
          count: 0,
          color: '#455a64'
        }
      ]
    },
    {
      title: 'License Management',
      items: [
        {
          name: 'License Vendors',
          description: 'Manage software license vendors (Microsoft, Adobe, Google, etc.)',
          icon: <LicenseIcon />,
          path: '/settings/license-vendors',
          count: 0,
          color: '#1565c0'
        },
        {
          name: 'License Products',
          description: 'Manage license products and pricing',
          icon: <LicenseIcon />,
          path: '/settings/license-products',
          count: 0,
          color: '#0277bd'
        },
        {
          name: 'License Assignments',
          description: 'Manage software licenses assigned to employees',
          icon: <LicenseIcon />,
          path: '/admin/license-assignments',
          count: 0,
          color: '#01579b'
        }
      ]
    },
    {
      title: 'Leave Management',
      items: [
        {
          name: 'Leave Types',
          description: 'Manage leave types (Annual, Sick, Maternity, etc.)',
          icon: <LeaveTypeIcon />,
          path: '/settings/leave-types',
          count: 7,
          color: '#5d4037'
        },
        {
          name: 'Leave Approvers',
          description: 'Configure users who receive and approve leave requests',
          icon: <LeaveTypeIcon />,
          path: '/settings/leave-approvers',
          count: 0,
          color: '#4e342e'
        },
        {
          name: 'Leave Balances',
          description: 'Review and edit leave balances by year',
          icon: <LeaveTypeIcon />,
          path: '/settings/leave-balances',
          count: 0,
          color: '#1565c0'
        },
        {
          name: 'Initial Leave Entry',
          description: 'Create initial approved leave requests (bulk)',
          icon: <LeaveTypeIcon />,
          path: '/settings/initial-leave-entry',
          count: 0,
          color: '#2e7d32'
        }
      ]
    },
    {
      title: 'System Settings',
      items: [
        {
          name: 'Manage Person Records',
          description: 'Create new or edit existing Person records',
          icon: <PermissionIcon />,
          path: '/people/new',
          count: 0,
          color: '#1976d2'
        },
        {
          name: 'Notifications & Templates',
          description: 'Configure who gets notified and edit email templates',
          icon: <PermissionIcon />,
          path: '/settings/notifications',
          count: 0,
          color: '#00796b'
        },
        {
          name: 'Profile Field Visibility',
          description: 'Configure which fields employees can see in their My Profile',
          icon: <PermissionIcon />,
          path: '/settings/profile-visibility',
          count: 0,
          color: '#7b1fa2'
        },
      ]
    },
    {
      title: 'Recruitment Management Reference Tables',
      items: [
        {
          name: 'Skill Categories',
          description: 'Manage recruitment skill categories (deprecate instead of delete)',
          icon: <PermissionIcon />,
          path: '/settings/skill-categories',
          count: 0,
          color: '#0288d1'
        },
        {
          name: 'Skills',
          description: 'Manage recruitment skills and their categories',
          icon: <PermissionIcon />,
          path: '/settings/skills',
          count: 0,
          color: '#009688'
        },
        {
          name: 'Position Skill Composer',
          description: 'Compose competency groups per position with drag & drop',
          icon: <PermissionIcon />,
          path: '/settings/position-skill-sets',
          count: 0,
          color: '#6a1b9a'
        }
      ]
    }
  ];

  const handleItemClick = (path) => {
    navigate(path);
  };

  return (
    <Box className="settings-container">
      {/* Header */}
      <Box className="settings-header">
        <Typography variant="h4" className="settings-title">
          System Configuration
        </Typography>
        <Typography variant="body1" className="settings-subtitle">
          Manage reference data and system settings
        </Typography>
      </Box>

      {/* Reference Data Sections */}
      <Box className="settings-content">
        {referenceDataSections.map((section, sectionIndex) => (
          <Card key={sectionIndex} className="settings-section-card">
            <CardContent>
              <Typography variant="h6" className="section-title">
                {section.title}
              </Typography>

              <List className="settings-list">
                {section.items.map((item, itemIndex) => (
                  <React.Fragment key={itemIndex}>
                    <ListItem disablePadding>
                      <ListItemButton
                        className="settings-list-item"
                        onClick={() => handleItemClick(item.path)}
                      >
                        <ListItemIcon
                          className="settings-item-icon"
                          sx={{ color: item.color }}
                        >
                          {item.icon}
                        </ListItemIcon>

                        <ListItemText
                          primary={
                            <Box className="settings-item-header">
                              <Typography variant="subtitle1" className="settings-item-name">
                                {item.name}
                              </Typography>
                              {item.count > 0 && (
                                <Chip
                                  label={item.count}
                                  size="small"
                                  className="settings-item-count"
                                  sx={{ backgroundColor: item.color, color: 'white' }}
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <Typography variant="body2" className="settings-item-description">
                              {item.description}
                            </Typography>
                          }
                        />
                      </ListItemButton>
                    </ListItem>

                    {itemIndex < section.items.length - 1 && (
                      <Divider variant="inset" component="li" />
                    )}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );
};

export default Settings;
